import {
  differenceInCalendarDays,
  format,
  parseISO,
  subDays,
  subYears,
} from "date-fns";

// MAIN-856: the admin picks a second window so both can be read off one chart.
export const COMPARISON_MODES = [
  { key: "none", label: "No comparison", shortLabel: null, description: null },
  {
    key: "period",
    label: "Previous period",
    shortLabel: "previous period",
    description:
      "Selected period compared with the equal-length window immediately before it.",
  },
  {
    key: "year",
    label: "Previous year",
    shortLabel: "last year",
    description:
      "Selected period compared with the same calendar dates one year earlier.",
  },
];

// Same-dates-last-year was the page's only behaviour before the picker existed.
export const DEFAULT_COMPARISON_MODE = "year";

const percent = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  signDisplay: "always",
});

const findMode = (mode) => COMPARISON_MODES.find((option) => option.key === mode);

export function comparisonModeLabel(mode) {
  return findMode(mode)?.shortLabel ?? null;
}

export function comparisonModeDescription(mode) {
  return findMode(mode)?.description ?? null;
}

/**
 * The secondary window for a selected range, or null when comparison is off.
 *
 * "period" steps back by the range's own length so the two windows are the
 * same width — a 30-day range compares against the 30 days before it, which is
 * rarely the previous calendar month. "year" keeps the same calendar dates,
 * which date-fns clamps (Feb 29 -> Feb 28) in a non-leap year.
 *
 * Granularity rides along so both queries bucket identically; without it the
 * API's auto-bucketing could split the windows across day/week/month and the
 * two series would no longer line up.
 */
export function comparisonRange(range, mode) {
  if (!range?.startDate || !range?.endDate) return null;
  if (mode !== "period" && mode !== "year") return null;

  const start = parseISO(range.startDate);
  const end = parseISO(range.endDate);
  const toApiDate = (date) => format(date, "yyyy-MM-dd");

  if (mode === "year") {
    return {
      startDate: toApiDate(subYears(start, 1)),
      endDate: toApiDate(subYears(end, 1)),
      granularity: range.granularity || null,
    };
  }

  const spanDays = differenceInCalendarDays(end, start) + 1;
  const priorEnd = subDays(start, 1);
  return {
    startDate: toApiDate(subDays(priorEnd, spanDays - 1)),
    endDate: toApiDate(priorEnd),
    granularity: range.granularity || null,
  };
}

/**
 * Folds the comparison buckets into the current series so recharts can draw
 * both from one dataset.
 *
 * Zipped by position rather than by date: the x-axis belongs to the selected
 * range, and the comparison bucket that sits at the same offset is the one it
 * is being compared against. Equal-length windows at a shared granularity make
 * this exact; a leap year can leave one unmatched bucket at the tail, which
 * simply renders without a comparison point.
 */
export function mergeComparisonSeries(series, comparisonSeries) {
  if (!series) return [];
  if (!comparisonSeries?.length) return series;

  return series.map((bucket, index) => {
    const compare = comparisonSeries[index];
    if (!compare) return bucket;
    return {
      ...bucket,
      compareOrderCount: compare.orderCount,
      compareGmv: compare.gmv,
      compareNet: compare.net,
      compareLabel: compare.label,
    };
  });
}

export function comparisonText(current, previous, modeLabel) {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);

  if (previousValue === 0) {
    return currentValue === 0
      ? `No change vs ${modeLabel}`
      : `New vs ${modeLabel}`;
  }

  const change =
    ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  return `${percent.format(change)}% vs ${modeLabel}`;
}
